import { create } from 'zustand';
import { api, apiErrorMessage, type Paginated } from '@/lib/api';
import { asArray, asNumber } from '@/lib/collections';
import { toast } from './ui.store';
import type { PlatformId } from '@ai-media/shared';
import type { AiBrief } from '@ai-media/shared';

export interface CampaignSummary {
  id: string;
  name: string;
  description: string | null;
  goal: string | null;
  platform_ids: PlatformId[];
  duration_days: number | null;
  posts_per_week: number;
  status: string;
  ai_brief: Record<string, unknown> | null;
  ai_plan: { items: Array<{ title: string; publishAt: string; platformIds: string[] }> } | null;
  published_at: string | null;
  error: string | null;
  post_count: number;
  created_at: string;
}

export interface CreateCampaignInput {
  name: string;
  description?: string;
  platformIds: PlatformId[];
  goal?: string;
  durationDays?: number;
  postsPerWeek?: number;
  startAt?: string;
  autoSchedule?: boolean;
  aiBrief?: { tone?: string; keywords?: string[]; audience?: string; objective?: string };
}

/** Partial update for an existing campaign — only the fields that changed. */
export interface UpdateCampaignInput {
  name?: string;
  description?: string | null;
  goal?: string | null;
  platformIds?: PlatformId[];
  durationDays?: number;
  startAt?: string;
  postsPerWeek?: number;
  autoSchedule?: boolean;
  aiBrief?: AiBrief;
}

interface CampaignState {
  campaigns: CampaignSummary[];
  total: number;
  loading: boolean;
  create: (input: CreateCampaignInput) => Promise<CampaignSummary | null>;
  update: (id: string, input: UpdateCampaignInput) => Promise<CampaignSummary | null>;
  publish: (id: string) => Promise<void>;
  pause: (id: string) => Promise<void>;
  deleteCampaign: (id: string) => Promise<void>;
  refresh: () => Promise<void>;
}

export const useCampaignStore = create<CampaignState>((set) => ({
  campaigns: [],
  total: 0,
  loading: false,

  refresh: async () => {
    set({ loading: true });
    try {
      const { data } = await api.get<Paginated<CampaignSummary>>('/campaigns', {
        params: { page: 1, limit: 50 },
      });
      set({
        // `platform_ids` is rendered as chips, so it must always be an array.
        campaigns: asArray(data?.data).map((campaign) => ({
          ...campaign,
          platform_ids: asArray(campaign.platform_ids),
        })),
        total: asNumber(data?.total),
      });
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    } finally {
      set({ loading: false });
    }
  },

  create: async (input) => {
    try {
      const { data } = await api.post<CampaignSummary>('/campaigns', input);
      toast('Campaign created with AI plan', 'success');
      await useCampaignStore.getState().refresh();
      return data;
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
      return null;
    }
  },

  update: async (id, input) => {
    try {
      const { data } = await api.patch<CampaignSummary>(`/campaigns/${id}`, input);
      toast('Campaign updated', 'success');
      await useCampaignStore.getState().refresh();
      return data;
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
      return null;
    }
  },

  publish: async (id) => {
    try {
      const { data } = await api.post<{ postsCreated: number }>(`/campaigns/${id}/publish`);
      toast(`Scheduled ${data.postsCreated} posts`, 'success');
      await useCampaignStore.getState().refresh();
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    }
  },

  pause: async (id) => {
    try {
      await api.post(`/campaigns/${id}/pause`);
      toast('Campaign paused', 'medium');
      await useCampaignStore.getState().refresh();
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    }
  },

  deleteCampaign: async (id) => {
    try {
      await api.delete(`/campaigns/${id}`);
      toast('Campaign deleted', 'success');
      await useCampaignStore.getState().refresh();
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    }
  },
}));
